import { useState } from 'react';
import { Drawer, Grid, Button, Tooltip, IconButton, Checkbox, FormControlLabel, Typography, Divider, Badge, Box } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import DateFilter from './DateFilter';
import TextFilter from './TextFilter';
import NumberFilter from './NumberFilter';
import DropdownFilter from './DropdownFilter';
import FilterOutput from './FilterOutput';

const removeEmptyKey = (obj) =>
    Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '')
    );

const FilterComponent = ({ icon, direction = 'right', tooltipContent = 'Filter', filter, filterMenu, getResponseBack, module, defaultFilter, }) => {
    const [filterOption, setFilterOption] = useState({});
    const [isOpen, setOpen] = useState(false);
    const activeFilterCount = filter ? Object.keys(removeEmptyKey(filter)).length : 0;
    const pendingCount = Object.keys(removeEmptyKey(filterOption)).length;

    const onFieldChange = (keyParam, value) => {
        const inputType = filterMenu[keyParam]?.inputType;
    
        let updated = {
            ...filterOption,
            [keyParam]: inputType === 'text' ? value.trimStart() : value,
        };
    
        const dependentFields = filterMenu[keyParam]?.dependentFields;
        if (dependentFields?.length) {
            dependentFields.forEach((field) => {
                delete updated[field]; 
            });
        }
    
        setFilterOption(updated);
        filterMenu?.onDependencyChange?.(keyParam, value);
    };


    const onRemoveFilter = (key) => {
        if (key === 'all') {
            setFilterOption({});
            getResponseBack({});
        } else {
            const newFilter = { ...filter };
            delete newFilter[key];
            const dependentFields = filterMenu[key]?.dependentFields;
            if (dependentFields?.length) {
                dependentFields.forEach((field) => {
                    delete newFilter[field];
                });
            }
            setFilterOption(newFilter);
            getResponseBack(newFilter);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setFilterOption(defaultFilter ? { ...defaultFilter, ...filter } : filter);
        filterMenu?.onOpen?.();
    };
    const handleClose = () => setOpen(false);

    return (
        <div className='filter-ui-component' style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px', minWidth: 0, overflow: 'hidden' }}>
            <Tooltip title={tooltipContent} arrow>
                <IconButton size='small' color='primary' onClick={handleOpen}>
                    <Badge
                        badgeContent={activeFilterCount}
                        color='primary'
                        overlap='circular'
                        sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '16px', height: '16px', padding: '0 4px' } }}
                    >
                        {icon || <FilterListIcon fontSize='small' />}
                    </Badge>
                </IconButton>
            </Tooltip>

            {filter && Object.keys(filter).length ? (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <FilterOutput
                        filter={filter}
                        filterMenu={filterMenu}
                        onRemoveFilter={onRemoveFilter}
                        module={module}
                    />
                </div>
            ) : null}

            <Drawer
                anchor={direction}
                open={isOpen}
                onClose={handleClose}
                slotProps={{ paper: { sx: { width: 350, display: 'flex', flexDirection: 'column', overflow: 'hidden' } } }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f7ff', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                            Filters
                        </Typography>
                        {activeFilterCount > 0 && (
                            <Box sx={{ bgcolor: '#1976d2', color: '#fff', borderRadius: '10px', px: 0.8, fontSize: '11px', fontWeight: 700, lineHeight: '18px', minWidth: '18px', textAlign: 'center' }}>
                                {activeFilterCount}
                            </Box>
                        )}
                    </Box>
                    <IconButton size='small' onClick={handleClose} sx={{ color: '#666', '&:hover': { bgcolor: '#ebebeb' } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1, pb: 1 }}>
                    <Grid container spacing={1.5}>
                        {Object.keys(filterMenu).map((keyParam, index) => {
                            const { inputType, md, disabledWhen } = filterMenu[keyParam];
                            const isDisabled = disabledWhen ? disabledWhen(filterOption) : false;

                            if (inputType === 'text') {
                                return (
                                    <Grid size={{ xs: 12 }} key={index}>
                                        <TextFilter
                                            filterOption={filterOption}
                                            filterMenu={filterMenu}
                                            keyParam={keyParam}
                                            onFieldChange={onFieldChange}
                                            placeHolder={filterMenu[keyParam].placeHolder}
                                        />
                                    </Grid>
                                );
                            }

                            if (inputType === 'number') {
                                return (
                                    <Grid size={{ xs: md || 12 }} key={index}>
                                        <NumberFilter
                                            filterOption={filterOption}
                                            filterMenu={filterMenu}
                                            keyParam={keyParam}
                                            onFieldChange={onFieldChange}
                                            placeHolder={filterMenu[keyParam].placeHolder}
                                        />
                                    </Grid>
                                );
                            }

                            if (inputType === 'dropdown') {
                                return (
                                    <Grid size={{ xs: 12 }} key={index}>
                                        <DropdownFilter
                                            filterOption={filterOption}
                                            filterMenu={filterMenu}
                                            keyParam={keyParam}
                                            onFieldChange={onFieldChange}
                                            placeHolder={filterMenu[keyParam].placeHolder}
                                            disabled={isDisabled}
                                        />
                                    </Grid>
                                );
                            }

                            if (inputType === 'date') {
                                return (
                                    <Grid size={{ xs: 12 }} key={index}>
                                        <DateFilter
                                            filterOption={filterOption}
                                            filterMenu={filterMenu}
                                            keyParam={keyParam}
                                            onFieldChange={onFieldChange}
                                        />
                                    </Grid>
                                );
                            }

                            if (inputType === 'checkbox') {
                                return (
                                    <Grid size={{ xs: 12 }} key={index}>
                                        <FormControlLabel
                                            sx={{ mt: 0.5, mb: 0, height:'15px' }}
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    color='primary'
                                                    checked={!!filterOption[keyParam]}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            onFieldChange(keyParam, true);
                                                        } else {
                                                            setFilterOption((prev) => {
                                                                const updated = { ...prev };
                                                                delete updated[keyParam];
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                />
                                            }
                                            label={<Typography sx={{ fontSize: '13px', fontFamily: 'Roboto-Regular' }}>{filterMenu[keyParam].title}</Typography>}
                                        />
                                    </Grid>
                                );
                            }

                            return null;
                        })}
                    </Grid>
                </Box>

                {/* Footer */}
                <Divider />
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, flexShrink: 0, bgcolor: '#fafafa' }}>
                    {pendingCount > 0 ? (
                        <Button
                            fullWidth
                            onClick={() => { const result = getResponseBack(removeEmptyKey(filterOption)); if (result !== false) handleClose(); }}
                            color='primary'
                            variant='contained'
                            size='small'
                            sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '13px', textTransform: 'none', py: 0.75, borderRadius: '4px' }}
                        >
                            Apply ({pendingCount})
                        </Button>
                    ) : null}
                    <Button
                        fullWidth
                        onClick={handleClose}
                        variant='outlined'
                        color='primary'
                        size='small'
                        sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '13px', textTransform: 'none', py: 0.75, borderRadius: '4px'}}
                    >
                        Cancel
                    </Button>
                </Box>
            </Drawer>
        </div>
    );
};

export default FilterComponent;
